/**
 * Carrera Club Membership JavaScript Module
 * 
 * Provides client-side functionality for club membership features
 */

(function() {
  'use strict';

  // Initialize Club Membership object (backwards compatible with both Carrera and Revell naming)
  window.CarreraClub = window.CarreraClub || {};
  window.RevellClub = window.RevellClub || window.CarreraClub; // Alias for compatibility

  /**
   * Check if current user is a club member
   * @returns {boolean}
   */
  CarreraClub.isClubMember = function() {
    return window.carreraClubMembership?.isClubMember || window.revellClubMembership?.isClubMember || false;
  };

  /**
   * Get membership type
   * @returns {string|null} 'monthly', 'yearly', 'test', or null
   */
  CarreraClub.getMembershipType = function() {
    return window.carreraClubMembership?.membershipType || window.revellClubMembership?.membershipType || null;
  };

  /**
   * Check if test mode is active
   * @returns {boolean}
   */
  CarreraClub.isTestMode = function() {
    return window.carreraClubMembership?.isTestMode || window.revellClubMembership?.isTestMode || false;
  };

  /**
   * Hide elements for non-club members
   */
  CarreraClub.hideNonMemberContent = function() {
    const elements = document.querySelectorAll('[data-club-members-only]');
    
    if (!this.isClubMember()) {
      elements.forEach(el => {
        el.style.display = 'none';
      });
    }
  };

  /**
   * Show club member badge/indicator
   */
  CarreraClub.showMemberBadge = function() {
    if (this.isClubMember()) {
      const badges = document.querySelectorAll('[data-club-member-badge]');
      badges.forEach(badge => {
        badge.style.display = 'block';
        
        if (this.isTestMode()) {
          badge.classList.add('club-test-mode');
          badge.setAttribute('title', 'Test Mode Active');
        }
      });
    }
  };

  /**
   * Apply club member discount to products
   * @param {number} price - Original price
   * @param {number} discount - Discount percentage (e.g., 10 for 10%)
   * @returns {number} Discounted price
   */
  CarreraClub.applyDiscount = function(price, discount) {
    if (!this.isClubMember()) {
      return price;
    }
    return price * (1 - discount / 100);
  };

  /**
   * Check if collection is exclusive to club members
   * @param {string} collectionHandle - Collection handle
   * @returns {boolean}
   */
  CarreraClub.isExclusiveCollection = function(collectionHandle) {
    const exclusiveCollections = window.carreraClubConfig?.exclusiveCollections || window.revellClubConfig?.exclusiveCollections || [];
    return exclusiveCollections.includes(collectionHandle);
  };

  // Alias all methods to RevellClub for backward compatibility
  RevellClub.hideNonMemberContent = CarreraClub.hideNonMemberContent;
  RevellClub.showMemberBadge = CarreraClub.showMemberBadge;
  RevellClub.applyDiscount = CarreraClub.applyDiscount;
  RevellClub.isExclusiveCollection = CarreraClub.isExclusiveCollection;
  RevellClub.isClubMember = CarreraClub.isClubMember;
  RevellClub.getMembershipType = CarreraClub.getMembershipType;
  RevellClub.isTestMode = CarreraClub.isTestMode;

  /**
   * Show restricted access message
   * @param {HTMLElement} container - Container element
   */
  CarreraClub.showRestrictedMessage = function(container) {
    if (!container) return;
    
    const message = document.createElement('div');
    message.className = 'club-restricted-message';
    message.innerHTML = `
      <div class="club-restricted-content">
        <svg class="club-lock-icon" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
          <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
        </svg>
        <h3>Club-Mitglieder Exklusiv</h3>
        <p>Dieser Inhalt ist nur für Club-Mitglieder verfügbar.</p>
        <a href="/products/club-membership" class="button button--primary">Jetzt Mitglied werden</a>
      </div>
    `;
    
    container.innerHTML = '';
    container.appendChild(message);
  };

  /**
   * Toggle test mode via URL parameter
   * Add ?club_test=true to URL to enable test mode
   */
  CarreraClub.toggleTestMode = function() {
    const urlParams = new URLSearchParams(window.location.search);
    const testMode = urlParams.get('club_test') === 'true';
    
    // If URL has test mode but Liquid didn't set it, override
    if (testMode && !window.carreraClubMembership?.isTestMode && !window.revellClubMembership?.isTestMode) {
      console.warn('⚠️ Test mode detected in URL but not in Liquid - overriding');
      window.carreraClubMembership = {
        isClubMember: true,
        membershipType: 'test',
        isTestMode: true
      };
    }
    
    if (testMode || this.isTestMode()) {
      console.log('🎭 Club Test Mode Active');
      console.log('Membership Type:', this.getMembershipType());
      console.log('Is Club Member:', this.isClubMember());
    }
  };

  /**
   * Initialize club membership features on page load
   */
  CarreraClub.init = function() {
    this.toggleTestMode();
    this.hideNonMemberContent();
    this.showMemberBadge();
    
    // Log membership status in console for debugging
    if (this.isClubMember()) {
      console.log('✅ Club Member Status: Active (' + this.getMembershipType() + ')');
    }
  };

  /**
   * Create visual indicator for test mode
   */
  CarreraClub.showTestModeIndicator = function() {
    if (!this.isTestMode()) {
      console.log('ℹ️ Test mode not active, banner not shown');
      return;
    }
    
    console.log('🎨 Creating test mode banner...');
    
    // Add styles first
    const style = document.createElement('style');
    style.textContent = `
      .club-test-mode-indicator {
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        right: 0 !important;
        z-index: 999999 !important;
        pointer-events: none !important;
      }
      
      .club-test-banner {
        background: #ff6b6b !important;
        color: white !important;
        text-align: center !important;
        padding: 12px 16px !important;
        font-size: 15px !important;
        font-weight: bold !important;
        box-shadow: 0 4px 12px rgba(0,0,0,0.25) !important;
        line-height: 1.4 !important;
      }
    `;
    document.head.appendChild(style);
    
    // Create indicator
    const indicator = document.createElement('div');
    indicator.className = 'club-test-mode-indicator';
    indicator.innerHTML = `
      <div class="club-test-banner">
        🎭 Test Mode: Club-Mitgliedschaft aktiv
      </div>
    `;
    
    // Wait for body to be available
    if (document.body) {
      document.body.insertBefore(indicator, document.body.firstChild);
      console.log('✅ Test mode banner added to page');
    } else {
      document.addEventListener('DOMContentLoaded', function() {
        document.body.insertBefore(indicator, document.body.firstChild);
        console.log('✅ Test mode banner added to page (after DOMContentLoaded)');
      });
    }
  };

  // Alias remaining methods
  RevellClub.showRestrictedMessage = CarreraClub.showRestrictedMessage;
  RevellClub.toggleTestMode = CarreraClub.toggleTestMode;
  RevellClub.init = CarreraClub.init;
  RevellClub.showTestModeIndicator = CarreraClub.showTestModeIndicator;

  // Auto-initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      CarreraClub.init();
      CarreraClub.showTestModeIndicator();
    });
  } else {
    CarreraClub.init();
    CarreraClub.showTestModeIndicator();
  }

})();

